// @ts-nocheck

import { useMemo, useState } from "react";
import { Link, useRouteLoaderData, useSearchParams } from "react-router-dom";
//-------------------------------------------
import { locatingItems } from "src/components/assignments/locatingForm";
import {
    numberReducer, 
    onlyUnique, 
    MyDate, 
    round, scroll, 
    arrayReducer, 
    exclusiveOutisdeKeys, 
    potholingReportWriterEmails, 
    locatingReportWriterEmails, 
    timeDurationCalc} from "src/components/common/functions";
import Chart from "./chart";
//-------------------------------------------
// this is for project stage color:
// 255, 99, 132 red
// 54, 162, 235 blue
// 255, 206, 86 yellow
// 75, 192, 192 green
// 153, 102, 255 purple
// 255, 159, 64 orange
// 127, 188, 65 grass green
// 50, 136, 189 dark blue
// 94, 79, 162 dark purple
//purchase category color 7 out of 9
//-------------------------------------------
export default function Dashboard(){
    const [searchParams, setSearchParams] = useSearchParams();
    const loaderData = useRouteLoaderData("Dashboard");
    const dashboardData = useMemo(
        () => (loaderData && typeof loaderData === "object" ? loaderData : {}),
        [loaderData]
    );
    const timeCards = useMemo(() => (Array.isArray(dashboardData.timeCards) ? dashboardData.timeCards : []), [dashboardData]);
    const works = useMemo(
        () => (
            Array.isArray(dashboardData.works) ? dashboardData.works :
            Array.isArray(dashboardData.work) ? dashboardData.work :
            []
        ),
        [dashboardData]
    );
    const assignments = useMemo(
        () => (Array.isArray(dashboardData.assignments) ? dashboardData.assignments : []),
        [dashboardData]
    );
    const dashboardPurchases = useMemo(() => (Array.isArray(dashboardData.purchases) ? dashboardData.purchases : []), [dashboardData]);
    const dashboardSummary = useMemo(() => (dashboardData.summary && typeof dashboardData.summary === "object" ? dashboardData.summary : {}), [dashboardData]);
    const projects = useMemo(
        () => (Array.isArray(dashboardData.projects) ? dashboardData.projects : []),
        [dashboardData]
    );
    const projectPipelines = useMemo(() => (Array.isArray(dashboardData.projectPipelines) ? dashboardData.projectPipelines : []), [dashboardData]);
    const projectStages = useMemo(() => (Array.isArray(dashboardData.projectStages) ? dashboardData.projectStages : []), [dashboardData]);
    const workCategories = useMemo(() => (Array.isArray(dashboardData.workCategories) ? dashboardData.workCategories : []), [dashboardData]);
    const purchaseCategories = useMemo(() => (Array.isArray(dashboardData.purchaseCategories) ? dashboardData.purchaseCategories : []), [dashboardData]);
    const purchaseDepartments = useMemo(() => (Array.isArray(dashboardData.purchaseDepartments) ? dashboardData.purchaseDepartments : []), [dashboardData]);
    const reportStatuses = useMemo(() => (Array.isArray(dashboardData.reportStatuses) ? dashboardData.reportStatuses : []), [dashboardData]);
    const recentPurchases = useMemo(() => (Array.isArray(dashboardData.recentPurchases) ? dashboardData.recentPurchases : []), [dashboardData]);
    const recentRentals = useMemo(() => (Array.isArray(dashboardData.recentRentals) ? dashboardData.recentRentals : []), [dashboardData]);
    const rentalVehicleCounts = useMemo(
        () => Object.entries(
            recentRentals.reduce((acc, item) => {
                const label = item?.equipmentName || item?.vehicle || item?.label || "Unknown Vehicle";
                acc[label] = (acc[label] || 0) + 1;
                return acc;
            }, {})
        )
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
        [recentRentals]
    );
    const projectRows = useMemo(
        () => (
            projects.length
                ? projects
                : works.filter((value, index, array) =>
                    value.projectId && array.findIndex(project => project.projectId === value.projectId) === index
                )
        ),
        [projects, works]
    );
    const pipelineRows = useMemo(
        () => (
            projectPipelines.length
                ? projectPipelines
                : projectRows
                .reduce((acc, project) => {
                    const label = project.pipeline || project.category || "Unspecified";
                    acc[label] = (acc[label] || 0) + 1;
                    return acc;
                }, {})
        ),
        [projectPipelines, projectRows]
    );
    const stageRows = useMemo(
        () => (
            projectStages.length
                ? projectStages
                : projectRows
                .reduce((acc, project) => {
                    const label = project.stage || "Unspecified";
                    acc[label] = (acc[label] || 0) + 1;
                    return acc;
                }, {})
        ),
        [projectStages, projectRows]
    );
    const normalizedPipelineRows = useMemo(
        () => Array.isArray(pipelineRows)
            ? pipelineRows
            : Object.entries(pipelineRows).map(([label, count]) => ({ label, count })),
        [pipelineRows]
    );
    const normalizedStageRows = useMemo(
        () => Array.isArray(stageRows)
            ? stageRows
            : Object.entries(stageRows).map(([label, count]) => ({ label, count })),
        [stageRows]
    );
    const locatingMap = Object.fromEntries(locatingItems.map(item => [item.title, item.showTitle]));
    //-------------------------------------------------------------------------
    const defaultSelectedDate = {
        start: `${(new Date()).getFullYear()}-${
            ((new Date()).getMonth() + 1) < 10?
            `0${(new Date()).getMonth() + 1}`
            :
            ((new Date()).getMonth() + 1)
        }-01`,
        end: (new MyDate()).s()
    }
    const appliedStart = searchParams.get("start") || defaultSelectedDate.start;
    const appliedEnd = searchParams.get("end") || defaultSelectedDate.end;
    const opt = [
        {value: "Work In Progress", rgb: [255, 99, 132]}, 
        {value: "Report Deliverable", rgb: [54, 162, 235]}, 
        {value: "Invoice", rgb: [255, 206, 86]}, 
        {value: "Received Payment", rgb: [75, 192, 192]}, 
        {value: "Progress Payment", rgb: [94, 79, 162]}, 
        {value: "Permits", rgb: [153, 102, 255]}, 
        {value: "USA Delineation", rgb: [255, 159, 64]}, 
        {value: "Schedule Field Work", rgb: [127, 188, 65]}, 
        {value: "Traffic Control Plans", rgb: [50, 136, 189]}
    ];
    const cardStyle = {
        background: "linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%)",
        border: "1px solid #d8e0ea",
        borderRadius: 18,
        boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
    };
    const summaryValueStyle = {
        fontSize: "1.8rem",
        lineHeight: 1.1,
        fontWeight: 700,
        color: "#102a43",
    };
    const sectionButtonStyle = (isActive) => ({
        border: "1px solid " + (isActive ? "#0d6efd" : "#d8e0ea"),
        background: isActive ? "#0d6efd" : "#ffffff",
        color: isActive ? "#ffffff" : "#486581",
        borderRadius: 999,
        padding: "0.5rem 0.95rem",
        fontWeight: 600,
        boxShadow: isActive ? "0 10px 20px rgba(13, 110, 253, 0.18)" : "none",
    });
    const palette = (index, alpha = 0.65) => `rgba(${(opt[index % opt.length]?.rgb ?? [13, 110, 253]).join(", ")}, ${alpha})`;
    //-------------------------------------------------------------------------
    const [open, setOpen] = useState("projects");
    const [start, setStart] = useState(appliedStart);
    const [end, setEnd] = useState(appliedEnd);
    const toggleSection = (section) => {
        setOpen(open === section ? "" : section);
        scroll(null);
    };
    const curReports = useMemo(() => {
        const locatingArr = [];
        const potholingArr = [];
        timeCards.forEach(u => Object.keys(u).sort().reduce(
            (obj, key) => {
                if(exclusiveOutisdeKeys.includes(key)){
                    obj[key] = u[key];
                    return obj;
                }
                obj[key] = u[key]?
                    {
                        ...JSON.parse(u[key]), 
                        email: obj.email
                    }
                    :
                    {
                        form: Array(7).fill({inOut: [], notes: ""}), 
                        email: obj.email, 
                        status: "Created"
                    }
                ;
                if(obj[key].status !== "Created"){
                    if(locatingReportWriterEmails.includes(obj[key].email)){
                        locatingArr.push(...obj[key].form.filter(f => f.inOut.length));
                    }
                    if(potholingReportWriterEmails.includes(obj[key].email)){
                        potholingArr.push(...obj[key].form.filter(f => f.inOut.length));
                    }
                }
                return obj;
            }, 
            {}
        ));
        const locatingReport = locatingArr.map(l => l.inOut).reduce(arrayReducer, []).filter(io => io.reports).map(io => io.reports).reduce(arrayReducer, []);
        const potholingReport = potholingArr.map(l => l.inOut).reduce(arrayReducer, []).filter(io => io.reports).map(io => io.reports).reduce(arrayReducer, []);
        return {
            locatingReportNumber: locatingReport.map(l => l.reportId).filter(onlyUnique).length,
            locatingReportHours: (locatingReport.map(l => timeDurationCalc(l.start, l.end)).reduce(numberReducer, 0) / 60).toFixed(2),
            potholingReportNumber: potholingReport.map(l => l.reportId).filter(onlyUnique).length,
            potholingReportHours: (potholingReport.map(l => timeDurationCalc(l.start, l.end)).reduce(numberReducer, 0) / 60).toFixed(2),
        };
    }, [timeCards]);
    const { sc, cctv, locating, potholing } = useMemo(() => {
        const dataSheets = assignments;
        const defaultValues = {
            sc: { scanningCoring: 0, scanning: 0, coring: 0 },
            cctv: {
                "Completed Lateral Footage (ft)": "0",
                "Completed Linear Footage (ft)": "0",
                "Total Sewer Footage Mark / Locate (ft)": "0",
                "Total Storm Footage Mark / Locate (ft)": "0"
            },
            locating: {
                "White (ft)": "0",
                "Pink (ft)": "0",
                "Red (ft)": "0",
                "Yellow (ft)": "0",
                "Orange (ft)": "0",
                "Blue (ft)": "0",
                "Purple (ft)": "0",
                "Green (ft)": "0",
                "Total (ft)": "0"
            },
            potholing: {
                pothole: 0,
                cancelPothole: 0,
                manhole: 0,
                cancelManhole: 0,
                electric: 0,
                cancelElectric: 0,
            }
        };
        if(!dataSheets.length) return defaultValues;
        const forms = dataSheets.map(d => JSON.parse(d.forms)).reduce(arrayReducer, []);
        const reduceCctv = (a, b) => {
            const obj = {};
            for(let key in defaultValues.cctv){
                obj[key] = window.Number.parseFloat(a[key] || "0") + window.Number.parseFloat(b[key] || "0");
            }
            return obj;
        };
        const reduceLocating = (a, b) => {
            const obj = {};
            for(let key in defaultValues.locating){
                obj[key] = window.Number.parseFloat(a[key] || "0") + window.Number.parseFloat(b[key] || "0");
            }
            return obj;
        };
        const cctvData = forms.filter(d => d.form === "CCTV Form").map(d => JSON.parse(d.content)).reduce((a, b) => reduceCctv(a, b), defaultValues.cctv);
        const locatingData = forms.filter(d => d.form === "Locating Form").map(d => JSON.parse(d.content)).reduce((a, b) => reduceLocating(a, b), defaultValues.locating);
        locatingData["Total (ft)"] = Object.values(locatingData).reduce((aa, bb) => window.Number.parseFloat(aa) + window.Number.parseFloat(bb));
        return {
            sc: {
                scanningCoring: dataSheets.filter(d => d.laborCategory === "GPR & Concrete Scaning & Coring").length,
                scanning: dataSheets.filter(d => d.laborCategory === "GPR & Concrete Scanning").length,
                coring: dataSheets.filter(d => d.laborCategory === "GPR & Concrete Coring").length
            },
            cctv: cctvData,
            locating: locatingData,
            potholing: {
                pothole: dataSheets.map(d => JSON.parse(d.potholingForm)).reduce(arrayReducer, []).filter(d => d.cancel !== "Yes").length,
                cancelPothole: dataSheets.map(d => JSON.parse(d.potholingForm)).reduce(arrayReducer, []).filter(d => d.cancel === "Yes").length,
                manhole: forms.filter(d => d.form === "Manhole Form" && d.cancel !== "Yes").length,
                cancelManhole: forms.filter(d => d.form === "Manhole Form" && d.cancel === "Yes").length,
                electric: forms.filter(d => d.form === "Electric Pothole Form" && d.cancel !== "Yes").length,
                cancelElectric: forms.filter(d => d.form === "Electric Pothole Form" && d.cancel === "Yes").length
            }
        };
    }, [assignments]);
    //-------------------------------------------------------------------------
    return (
    <div className = "cssContent pb8rem">
            <section className = "my-3 p-4" style = {cardStyle}>
                <div className = "d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3">
                    <div>
                        <div className = "text-uppercase text-primary fw-bold" style = {{letterSpacing: "0.08em", fontSize: "0.78rem"}}>Operations Snapshot</div>
                        <div className = "text-muted">Operational activity from <strong>{appliedStart}</strong> to <strong>{appliedEnd}</strong>.</div>
                    </div>
                    <div className = "d-flex flex-wrap align-items-end gap-3">
                <span className = "d-flex align-items-center">
                    <label htmlFor = "start" className = "fw-semibold text-muted">Start:</label>
                    <input
                        id = "start"
                        type = "date"
                        min = "1900-01-01"
                        max = {(new MyDate()).s() || "2999-12-31"}
                        className = "form-control w-auto ms-2"
                        value = {start}
                        onChange = {e => setStart(e.currentTarget.value)}
                        name = "start"
                    />
                </span>
                <span className = "d-flex align-items-center">
                    <label htmlFor = "end" className = "fw-semibold text-muted">End:</label>
                    <input
                        id = "end"
                        type = "date"
                        min = "1900-01-01"
                        max = {(new MyDate()).s() || "2999-12-31"}
                        className = "form-control w-auto ms-2"
                        value = {end}
                        onChange = {e => setEnd(e.currentTarget.value)}
                        name = "end"
                    />
                </span>
                {appliedStart !== start || appliedEnd !== end?
                <button
                    type = "button"
                    className = "btn btn-primary"
                    onClick = {() => {
                        if(start === ""){
                            window.alert("Start can not be empty.");
                            return
                        }
                        if(end === ""){
                            window.alert("End can not be empty.");
                            return
                        }
                        if(appliedStart === start && appliedEnd === end){
                            return;
                        }
                        if(new Date(start?.replace(/-/g, "/")) <= new Date(end?.replace(/-/g, "/"))){
                            setSearchParams({ start, end });
                        }else{
                            window.alert("Start date is greater than end date.");
                            return;
                        }
                    }}
                >
                    Load
                </button>: null}
                    </div>
                </div>
            </section>
            <div>
            <div className = "row g-3 mb-4">
                {[
                    { label: "Projects", value: dashboardSummary.projectsCreated ?? normalizedPipelineRows.map(item => item.count || 0).reduce(numberReducer, 0) },
                    { label: "Works", value: dashboardSummary.worksCreated ?? works.length },
                    { label: "Assignments", value: assignments.length },
                    { label: "POs", value: dashboardSummary.purchasesCreated ?? dashboardPurchases.length },
                    { label: "Report Hours", value: `${curReports.locatingReportHours || "0.00"} / ${curReports.potholingReportHours || "0.00"}` },
                ].map((item, index) =>
                <div key = {index} className = "col-12 col-sm-6 col-xl">
                    <div className = "h-100 p-3" style = {cardStyle}>
                        <div className = "text-uppercase text-muted fw-bold mb-2" style = {{fontSize: "0.74rem", letterSpacing: "0.08em"}}>{item.label}</div>
                        <div style = {summaryValueStyle}>{item.value}</div>
                    </div>
                </div>)}
            </div>
            <div className = "d-flex flex-wrap gap-2 mb-4">
                <button type = "button" style = {sectionButtonStyle(open === "projects")} onClick = {() => toggleSection("projects")}>Projects</button>
                <button type = "button" style = {sectionButtonStyle(open === "purchases")} onClick = {() => toggleSection("purchases")}>Purchases</button>
                <button type = "button" style = {sectionButtonStyle(open === "operations")} onClick = {() => toggleSection("operations")}>Operations</button>
            </div>
            <div className = "py-4">
                {open === "projects" && (
                <div className = "row g-4">
                    <div className = "col-12 col-xl-7">
                        <div className = "h-100 p-4" style = {cardStyle}>
                            <Chart
                                width = "91%"
                                chartConfig = {{
                                    type: "doughnut",
                                    data: {
                                        labels: normalizedPipelineRows.map(item => item.label),
                                        datasets: [{
                                            data: normalizedPipelineRows.map(item => item.count || 0),
                                            backgroundColor: normalizedPipelineRows.map((_, index) => palette(index, 0.55)),
                                            borderColor: normalizedPipelineRows.map((_, index) => palette(index, 1)),
                                            borderWidth: 1,
                                        }]
                                    },
                                    options: {
                                        title: {
                                            fontSize: 20,
                                            display: true,
                                            text: "Projects by Category"
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <div className = "col-12 col-xl-5">
                        <div className = "h-100 p-4" style = {cardStyle}>
                            <Chart
                                width = "91%"
                                chartConfig = {{
                                    type: "bar",
                                    data: {
                                        labels: normalizedStageRows.map(item => item.label),
                                        datasets: [{
                                            label: "Projects",
                                            data: normalizedStageRows.map(item => item.count || 0),
                                            backgroundColor: normalizedStageRows.map((_, index) => palette(index, 0.55)),
                                            borderColor: normalizedStageRows.map((_, index) => palette(index, 1)),
                                            borderWidth: 1,
                                        }]
                                    },
                                    options: {
                                        legend: { display: false },
                                        title: {
                                            fontSize: 20,
                                            display: true,
                                            text: "Projects by Stage"
                                        },
                                        scales: {
                                            yAxes: [{
                                                ticks: { beginAtZero: true, precision: 0 }
                                            }]
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <div className = "col-12">
                        <div className = "h-100 p-4" style = {cardStyle}>
                            <Chart
                                width = "96%"
                                chartConfig = {{
                                    type: "bar",
                                    data: {
                                        labels: workCategories.map(item => item.label),
                                        datasets: [{
                                            label: "Works",
                                            data: workCategories.map(item => item.count || 0),
                                            backgroundColor: workCategories.map((_, index) => palette(index, 0.55)),
                                            borderColor: workCategories.map((_, index) => palette(index, 1)),
                                            borderWidth: 1,
                                        }]
                                    },
                                    options: {
                                        legend: { display: false },
                                        title: {
                                            fontSize: 20,
                                            display: true,
                                            text: "Works by Category"
                                        },
                                        scales: {
                                            yAxes: [{
                                                ticks: { beginAtZero: true, precision: 0 }
                                            }]
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <div className = "col-12 col-xl-6">
                        <div className = "h-100 p-4" style = {cardStyle}>
                            <h5 className = "mb-3">Stage Summary</h5>
                            <div className = "d-flex flex-column gap-2">
                                {normalizedStageRows.map((item, index) =>
                                <div key = {index} className = "d-flex justify-content-between align-items-center border rounded-3 px-3 py-2">
                                    <span>{item.label}</span>
                                    <span className = "fw-bold">{item.count || 0}</span>
                                </div>)}
                            </div>
                        </div>
                    </div>
                    <div className = "col-12 col-xl-6">
                        <div className = "h-100 p-4" style = {cardStyle}>
                            <h5 className = "mb-3">Projects</h5>
                            {projectRows.length ? (
                            <div className = "d-flex flex-column gap-2">
                                {projectRows.map((project, index) =>
                                <Link
                                    key = {project.projectId || index}
                                    to = {`/Projects/${project.projectId}`}
                                    className = "border rounded-3 px-3 py-2 text-decoration-none d-flex justify-content-between align-items-center"
                                >
                                    <span>
                                        <span className = "fw-semibold text-dark">{project.projectName || "Untitled Project"}</span>
                                        <span className = "text-muted ms-2">{project.pipeline || project.category || ""}</span>
                                    </span>
                                    <span className = "fw-bold text-primary">{project.stage || ""}</span>
                                </Link>)}
                            </div>
                            ) : (
                            <div className = "text-muted">No project details in this range.</div>
                            )}
                        </div>
                    </div>
                </div>
                )}
                {open === "purchases" && (
                <div className = "row g-4">
                    <div className = "col-12 col-xl-7">
                        <div className = "h-100 p-4" style = {cardStyle}>
                            <Chart
                                width = "96%"
                                chartConfig = {{
                                    type: "bar",
                                    data: {
                                        labels: purchaseDepartments.map(item => item.label),
                                        datasets: [{
                                            label: "Total",
                                            data: purchaseDepartments.map(item => item.total || 0),
                                            backgroundColor: purchaseDepartments.map((_, index) => palette(index, 0.55)),
                                            borderColor: purchaseDepartments.map((_, index) => palette(index, 1)),
                                            borderWidth: 1,
                                        }]
                                    },
                                    options: {
                                        legend: { display: false },
                                        title: {
                                            fontSize: 20,
                                            display: true,
                                            text: "Purchases by Department"
                                        },
                                        scales: {
                                            yAxes: [{
                                                ticks: { beginAtZero: true }
                                            }]
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <div className = "col-12 col-xl-5">
                        <div className = "h-100 p-4" style = {cardStyle}>
                            <Chart
                                width = "91%"
                                chartConfig = {{
                                    type: "doughnut",
                                    data: {
                                        labels: purchaseCategories.map(item => item.label),
                                        datasets: [{
                                            data: purchaseCategories.map(item => item.total || 0),
                                            backgroundColor: purchaseCategories.map((_, index) => palette(index, 0.55)),
                                            borderColor: purchaseCategories.map((_, index) => palette(index, 1)),
                                            borderWidth: 1,
                                        }]
                                    },
                                    options: {
                                        title: {
                                            fontSize: 20,
                                            display: true,
                                            text: "Purchases by Category"
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <div className = "col-12 col-xl-6">
                        <div className = "h-100 p-4" style = {cardStyle}>
                            <h5 className = "mb-3">Department Totals</h5>
                            <div className = "d-flex flex-column gap-2">
                                {purchaseDepartments.map((item, index) =>
                                <div key = {index} className = "d-flex justify-content-between align-items-center border rounded-3 px-3 py-2">
                                    <span>{item.label}</span>
                                    <span className = "fw-bold">${round(item.total || 0, 2)}</span>
                                </div>)}
                            </div>
                        </div>
                    </div>
                    <div className = "col-12 col-xl-6">
                        <div className = "h-100 p-4" style = {cardStyle}>
                            <h5 className = "mb-3">Recent POs</h5>
                            <div className = "d-flex flex-column gap-2">
                                {recentPurchases.map((item, index) =>
                                <Link key = {index} to = {`/Purchases/${item.id}`} className = "border rounded-3 px-3 py-2 text-decoration-none d-flex justify-content-between align-items-center">
                                    <span>
                                        <span className = "fw-semibold text-dark">{item.id}</span>
                                        <span className = "text-muted ms-2">{item.projectName}</span>
                                    </span>
                                    <span className = "fw-bold text-success">${round(item.total || 0, 2)}</span>
                                </Link>)}
                            </div>
                        </div>
                    </div>
                </div>
                )}
                {open === "operations" && (
                <div className = "row g-4">
                    <div className = "col-12 col-xl-6">
                        <div className = "h-100 p-4" style = {cardStyle}>
                            <Chart
                                width = "96%"
                                chartConfig = {{
                                    type: "bar",
                                    data: {
                                        labels: reportStatuses.map(item => item.label),
                                        datasets: [{
                                            label: "Assignments",
                                            data: reportStatuses.map(item => item.count || 0),
                                            backgroundColor: reportStatuses.map((_, index) => palette(index, 0.55)),
                                            borderColor: reportStatuses.map((_, index) => palette(index, 1)),
                                            borderWidth: 1,
                                        }]
                                    },
                                    options: {
                                        legend: { display: false },
                                        title: {
                                            fontSize: 20,
                                            display: true,
                                            text: "Assignment Statuses"
                                        },
                                        scales: {
                                            yAxes: [{
                                                ticks: { beginAtZero: true, precision: 0 }
                                            }]
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <div className = "col-12 col-xl-6">
                        <div className = "h-100 p-4" style = {cardStyle}>
                            <div style = {{overflowX: "auto", overflowY: "hidden"}}>
                                <Chart
                                    width = {`${Math.max(rentalVehicleCounts.length * 110, 520)}px`}
                                    chartConfig = {{
                                        type: "bar",
                                        data: {
                                            labels: rentalVehicleCounts.map(item => item.label),
                                            datasets: [{
                                                label: "Number of Times",
                                                data: rentalVehicleCounts.map(item => item.count || 0),
                                                backgroundColor: rentalVehicleCounts.map((_, index) => palette(index, 0.55)),
                                                borderColor: rentalVehicleCounts.map((_, index) => palette(index, 1)),
                                                borderWidth: 1,
                                            }]
                                        },
                                        options: {
                                            legend: { display: false },
                                            title: {
                                                fontSize: 20,
                                                display: true,
                                                text: "Vehicle Usage"
                                            },
                                            scales: {
                                                xAxes: [{
                                                    ticks: {
                                                        autoSkip: false,
                                                        minRotation: 45,
                                                        maxRotation: 45,
                                                    },
                                                    scaleLabel: {
                                                        display: true,
                                                        labelString: "Vehicle",
                                                    }
                                                }],
                                                yAxes: [{
                                                    ticks: { beginAtZero: true, precision: 0 },
                                                    scaleLabel: {
                                                        display: true,
                                                        labelString: "Number of Times",
                                                    }
                                                }]
                                            }
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className = "col-12 col-xl-6">
                        <div className = "h-100 p-4" style = {cardStyle}>
                            <h5 className = "mb-3">Recent Rentals</h5>
                            <div className = "d-flex flex-column gap-2">
                                {recentRentals.map((item, index) =>
                                <div key = {index} className = "border rounded-3 px-3 py-2 d-flex justify-content-between align-items-center">
                                    <span>
                                        <span className = "fw-semibold">{item.equipmentName}</span>
                                        <span className = "text-muted ms-2">{item.projectName}</span>
                                    </span>
                                    <span className = "fw-bold text-capitalize">{item.status}</span>
                                </div>)}
                            </div>
                        </div>
                    </div>
                    <div className = "col-12 col-xl-6">
                        <div className = "h-100 p-4" style = {cardStyle}>
                            <h5 className = "mb-3">Report Tech</h5>
                            <div className = "row g-3">
                                <div className = "col-6">
                                    <div className = "border rounded-3 p-3 h-100">
                                        <div className = "text-muted small text-uppercase fw-bold mb-2">Locating</div>
                                        <div>Reports: <strong>{curReports.locatingReportNumber}</strong></div>
                                        <div>Hours: <strong>{curReports.locatingReportHours}</strong></div>
                                    </div>
                                </div>
                                <div className = "col-6">
                                    <div className = "border rounded-3 p-3 h-100">
                                        <div className = "text-muted small text-uppercase fw-bold mb-2">Potholing</div>
                                        <div>Reports: <strong>{curReports.potholingReportNumber}</strong></div>
                                        <div>Hours: <strong>{curReports.potholingReportHours}</strong></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className = "col-12 col-xl-6">
                        <div className = "h-100 p-4" style = {cardStyle}>
                            <h5 className = "mb-3">Potholing</h5>
                            <ul className = "mb-0">
                                <li>Potholes: {potholing.pothole} / Canceled: {potholing.cancelPothole}</li>
                                <li>Manholes: {potholing.manhole} / Canceled: {potholing.cancelManhole}</li>
                                <li>Electric: {potholing.electric} / Canceled: {potholing.cancelElectric}</li>
                            </ul>
                        </div>
                    </div>
                    <div className = "col-12 col-xl-6">
                        <div className = "h-100 p-4" style = {cardStyle}>
                            <h5 className = "mb-3">Locating</h5>
                            <ul className = "mb-0">
                                {Object.entries(locating).map((entry, entryIndex) => <li key = {entryIndex}>
                                    {locatingMap[entry[0]] ?? entry[0]}: {entry[1]}
                                </li>)}
                            </ul>
                        </div>
                    </div>
                    <div className = "col-12 col-xl-6">
                        <div className = "h-100 p-4" style = {cardStyle}>
                            <h5 className = "mb-3">CCTV</h5>
                            <ul className = "mb-0">
                                {Object.entries(cctv).map((entry, entryIndex) => <li key = {entryIndex}>
                                    {entry[0]}: {entry[1]}
                                </li>)}
                            </ul>
                        </div>
                    </div>
                    <div className = "col-12 col-xl-6">
                        <div className = "h-100 p-4" style = {cardStyle}>
                            <h5 className = "mb-3">GPR & Concrete Scanning and Coring</h5>
                            <ul className = "mb-0">
                                <li>Scanning & Coring: {sc.scanningCoring}</li>
                                <li>Scanning: {sc.scanning}</li>
                                <li>Coring: {sc.coring}</li>
                            </ul>
                        </div>
                    </div>
                </div>
                )}
            </div>
            </div>
    </div>
    )
}
